import { graphql } from '@/__generated__';
import { GenericMaterializedFilter } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';

export const GetMetricsByEnvironment = graphql(/* GraphQL */ `
  query Metrics(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $filter: GenericMaterializedFilter
  ) {
    environment(id: $environmentId, userId: $userId) {
      definition {
        lastUpdatedAt
        metrics(first: $first, after: $after, filter: $filter) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              name
              uniqueId
              type
              description
            }
          }
        }
      }
    }
  }
`);

type MetricsByEnvironmentParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  userId?: number | 'loading' | null;
  filter?: GenericMaterializedFilter;
  pause?: boolean;
};

/**
 * Metrics are Definition Only
 */
export const useDiscoveryDefinitionMetrics = ({
  first,
  after,
  environmentId,
  userId,
  filter,
  pause,
}: MetricsByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetMetricsByEnvironment,
      variables: {
        first,
        after,
        environmentId,
        userId: userId as number | null,
        filter,
      },
      pause: pause || !environmentId || !first || userId === 'loading',
    },
    (data) => {
      const metrics = data.environment.definition?.metrics;
      const pageInfo = metrics?.pageInfo;
      if (metrics == null || pageInfo == null) return undefined;
      return {
        totalCount: metrics.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return data.environment.definition?.metrics.edges.map((edge) => edge.node);
    },
    (data) => data.environment.definition?.lastUpdatedAt,
  );

  useCacheMonitor({ definition: query.lastUpdatedAt });

  return query;
};
